import React, { useState, useEffect } from 'react';
import { typesRedux as types } from '../../../../shared/types/typesRedux';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, FormLabel, Grid, Stack, Switch, Typography, TextField, MenuItem } from '@mui/material';
import FieldText from '../../../components/general/fields/fieldText';
import {
    asyncPatchSurvey,
    asyncPostSurvey,
} from '../../../../services/survey.service';
import { SwalCustomClass } from '../../../components/general/toastSwal/swalCustomClass';
import { MasterMessage } from '../../../../masters/masterMessages';
import { useNavigate } from 'react-router-dom';
import { SURVEYEDIT } from '../../../../routes/paths';
import {
    ProjectInterface,
    ProjectsList,
} from '../../../../shared/types/project.interface';
import MultipleAutocompleteField from '../../../components/general/fields/multipleAutocompleteField';
import DropZoneField from '../../../components/general/fields/dropzoneField';
import Swal from 'sweetalert2';

type Props = {
    idSurvey?: any;
    surveyInfo?: any;
    onSubmitSave: (value: boolean) => void;
    saveInfo?: any;
    onSave: (value: boolean) => void;
    projects?: ProjectsList[];
};

const SurveyInformation = ({
    idSurvey,
    surveyInfo,
    onSubmitSave,
    saveInfo,
    onSave,
    projects,
}: Props) => {
    const dispatch: any = useDispatch();
    const navigate = useNavigate();
    const { surveys }: any = MasterMessage;
    const [submit, setSubmit] = useState<boolean>(false);

    const formik = useFormik({
        initialValues: {
            code: idSurvey ? surveyInfo?.code : '',
            title: idSurvey ? surveyInfo?.title : '',
            description: idSurvey ? surveyInfo?.description : '',
            status: idSurvey ? surveyInfo?.status : 'in process',
            withTemplate: surveyInfo ? Boolean(surveyInfo.withTemplate) : true,
            projects: surveyInfo !== undefined ? surveyInfo.projectIds : [],
            logo: surveyInfo !== undefined ? surveyInfo.logo : undefined,
            headerImage:
                surveyInfo !== undefined ? surveyInfo.headerImage : undefined,
            themeColor: surveyInfo?.themeColor || '',
            fontFamily: surveyInfo?.fontFamily || '',
            headerImageHeight: surveyInfo?.headerImageHeight || '560',
        },
        validationSchema: Yup.object({
            code: Yup.string().required('El código es obligatorio'),
            title: Yup.string().required('El titulo es obligatorio'),
            projects: Yup.array()
                .required('El campo proyecto es requerido')
                .min(1, 'Debes seleccionar al menos una proyecto'),
        }),
        onSubmit: async (formSurveyInfo) => {
            formSurveyInfo.projects = formSurveyInfo.projects.map(
                (item: ProjectInterface) => item.id
            );
            setSubmit(true);
            if (!idSurvey) {
                const body: any = await asyncPostSurvey(formSurveyInfo).finally(
                    () => setSubmit(false)
                );
                if (body?.items !== undefined) {
                    navigate(`${SURVEYEDIT}/${body.items.id}`);
                    dispatch({ type: types.nextStepper });
                } else {
                    SwalCustomClass.fire({
                        text: surveys[body.message],
                        icon: 'error',
                        iconColor: '#1d1d1d',
                    });
                }
            } else {
                asyncPatchSurvey(idSurvey, formSurveyInfo)
                    .then((body: any) => {
                        if (body?.items !== undefined) {
                            onSave(true);
                            dispatch({ type: types.nextStepper });
                            window.setTimeout(() => {
                                onSave(false);
                            }, 500);
                        } else {
                            SwalCustomClass.fire({
                                text: surveys[body.message],
                                icon: 'error',
                                iconColor: '#1d1d1d',
                            });
                        }
                    })
                    .finally(() => setSubmit(false));
            }
        },
    });

    const handleChangeLogo = async (value: FormData[]) => {
        const file = value?.[0]?.get('file') as File;

        if (!file) {
            formik.setFieldValue('logo', null);
            return;
        }

        if (formik.values.logo) {
            SwalCustomClass.fire({
                text: 'Ya existe un logo cargado. Debes eliminarlo antes de subir otro.',
                icon: 'warning',
            });
            return;
        }

        const reader = new FileReader();

        reader.onload = (event) => {
            const img = event?.target?.result?.toString();

            formik.setValues({
                ...formik.values,
                logo: img,
                headerImage: null,
                withTemplate: true,
            });
        };

        reader.readAsDataURL(file);
    };

    const handleChangeHeader = async (value: FormData[]) => {
        const file = value?.[0]?.get('file') as File;

        if (!file) {
            formik.setFieldValue('headerImage', null);
            return;
        }

        if (formik.values.headerImage) {
            SwalCustomClass.fire({
                text: 'Ya existe una imagen de encabezado. Debes eliminarla antes de subir otra.',
                icon: 'warning',
            });
            return;
        }

        const reader = new FileReader();

        reader.onload = (event) => {
            const img = event?.target?.result?.toString();

            formik.setValues({
                ...formik.values,
                headerImage: img,
                logo: null,
                withTemplate: false,
            });
        };

        reader.readAsDataURL(file);
    };

    const confirmDelete = async (
        field: 'logo' | 'headerImage',
        opts?: {
            title?: string;
            text?: string;
            successText?: string;
        }
    ) => {
        let title = opts?.title;
        if (!title) {
            title =
                field === 'logo'
                    ? '¿Eliminar logo?'
                    : '¿Eliminar imagen de encabezado?';
        }

        let text = opts?.text;
        if (!text) {
            text =
                field === 'logo'
                    ? 'Esta acción eliminará el logo cargado.'
                    : 'Esta acción eliminará la imagen de encabezado.';
        }

        const result = await Swal.fire({
            title,
            text,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33',
        });

        if (result.isConfirmed) {
            const newValues: any = { ...formik.values };
            newValues[field] = null;
            newValues.withTemplate = true;

            formik.setValues(newValues);

            const successText =
                opts?.successText ??
                (field === 'logo'
                    ? 'Logo eliminado correctamente'
                    : 'Imagen eliminada correctamente');

            SwalCustomClass.fire({
                icon: 'success',
                text: successText,
            });
        }
    };
    useEffect(() => {
        onSubmitSave(submit);
        saveInfo(() => formik.handleSubmit);
    }, [submit, onSubmitSave, saveInfo]);

    useEffect(() => {
        if (formik.values.logo && formik.values.headerImage) {
            formik.setFieldValue('headerImage', null);
        }
    }, [formik.values.logo, formik.values.headerImage]);

    return (
        <>
            <Box>
                <Grid
                    container
                    spacing={{ xs: 1.5, md: 1.5 }}
                    columnSpacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
                >
                    <Grid item xs={12} md={6}>
                        <FieldText
                            label="Código Encuesta"
                            name="code"
                            size="small"
                            required={true}
                            touched={formik.touched.code}
                            errors={formik.errors.code}
                            value={formik.values.code}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <MultipleAutocompleteField
                            data={projects}
                            label="proyecto"
                            name="projects"
                            size="small"
                            required={true}
                            touched={formik.touched.projects}
                            errors={formik.errors.projects as React.ReactNode}
                            value={formik.values.projects}
                            onChangeValue={(value) =>
                                formik.setFieldValue('projects', value)
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FieldText
                            label="Titulo Encuesta"
                            name="title"
                            size="small"
                            required={true}
                            touched={formik.touched.title}
                            errors={formik.errors.title}
                            value={formik.values.title}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    {!formik.values.headerImage && (
                        <Grid item xs={12} mt={3} sx={{ minWidth: 300 }}>
                            <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                sx={{ mb: 1, color: 'black' }}
                            >
                                Imagen Logo
                            </Typography>
                            {formik.values.logo ? (
                                <Box>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            maxWidth: 300,
                                            margin: '0 auto',
                                            padding: '10px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            borderRadius: 2,
                                            border: '1px solid #ddd',
                                            backgroundColor: '#f5f5f5',
                                            mb: 1,
                                        }}
                                    >
                                        <img
                                            src={formik.values.logo}
                                            alt="logo"
                                            style={{
                                                maxWidth: '100%',
                                                height: 'auto',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </Box>

                                    <Typography
                                        sx={{ cursor: 'pointer', color: 'red' }}
                                        onClick={() => confirmDelete('logo')}
                                    >
                                        Eliminar logo
                                    </Typography>

                                    <Typography
                                        fontSize="12px"
                                        color="gray"
                                        sx={{ mt: 0.5 }}
                                    >
                                        Tamaño recomendado: 400 x 120 px
                                    </Typography>
                                </Box>
                            ) : (
                                !formik.values.headerImage && (
                                    <DropZoneField
                                        name="logo"
                                        title="Si desea usar un logo personalizado, arrastre y suelte una imagen aquí,"
                                        subtitle="o haga clic para seleccionar una"
                                        maxFiles={1}
                                        accept="image/*"
                                        onChange={handleChangeLogo}
                                        maxSizeMB={5}
                                    />
                                )
                            )}
                        </Grid>
                    )}
                    {!formik.values.logo && (
                        <Grid item xs={12} mt={3} sx={{ minWidth: 300 }}>
                            <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                sx={{ mb: 1, color: 'black' }}
                            >
                                Imagen de Encabezado
                            </Typography>
                            {formik.values.headerImage ? (
                                <Box>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            maxWidth: 600,
                                            margin: '0 auto',
                                            aspectRatio: '2.14 / 1',
                                            overflow: 'hidden',
                                            borderRadius: 2,
                                            border: '1px solid #ddd',
                                            backgroundColor: '#f5f5f5',
                                            mb: 1,
                                        }}
                                    >
                                        <img
                                            src={formik.values.headerImage}
                                            alt="header"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </Box>

                                    <Typography
                                        sx={{ cursor: 'pointer', color: 'red' }}
                                        onClick={() =>
                                            confirmDelete('headerImage')
                                        }
                                    >
                                        Eliminar imagen de encabezado
                                    </Typography>
                                    <Typography
                                        fontSize="12px"
                                        color="gray"
                                        sx={{ mt: 0.5 }}
                                    >
                                        Tamaño recomendado: 1200 x 375 px (16:5)
                                    </Typography>
                                </Box>
                            ) : (
                                !formik.values.logo && (
                                    <DropZoneField
                                        name="headerImage"
                                        title="Si desea usar una imagen de encabezado, arrastre y suelte una imagen aquí,"
                                        subtitle="o haga clic para seleccionar una"
                                        maxFiles={1}
                                        accept="image/*"
                                        onChange={handleChangeHeader}
                                        maxSizeMB={30}
                                    />
                                )
                            )}
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <FieldText
                            label="Descripcion Encuesta"
                            name="description"
                            required={false}
                            rows={5}
                            touched={formik.touched.description}
                            errors={formik.errors.description}
                            value={formik.values.description}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container direction="row">
                            <Grid item xs={8} md={6} mt={0.8}>
                                <FormLabel component="legend">
                                    Desea usar el tema por defecto de Comware:{' '}
                                </FormLabel>
                            </Grid>
                            <Grid item xs={4} md={6}>
                                <Stack direction="row" alignItems="center">
                                    <Typography>No</Typography>
                                    <Switch
                                        name="withTemplate"
                                        checked={formik.values.withTemplate}
                                        value={formik.values.withTemplate}
                                        onChange={formik.handleChange}
                                        disabled={Boolean(
                                            formik.values.headerImage
                                        )}
                                    />
                                    <Typography>Si</Typography>
                                </Stack>
                                {formik.values.headerImage && (
                                    <Typography
                                        fontSize="12px"
                                        color="gray"
                                        sx={{ mt: 0.5, ml: 0.5 }}
                                    >
                                        El tema de Comware se desactiva cuando
                                        se utiliza una imagen de encabezado.
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                            Tema de Encuesta
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Color del tema"
                            name="themeColor"
                            type="color"
                            value={formik.values.themeColor || '#3D91FF'}
                            onChange={formik.handleChange}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            select
                            size="small"
                            label="Tipo de letra"
                            name="fontFamily"
                            value={formik.values.fontFamily || ''}
                            onChange={formik.handleChange}
                        >
                            <MenuItem value="">Por defecto</MenuItem>
                            <MenuItem value="Arial">Arial</MenuItem>
                            <MenuItem value="Roboto">Roboto</MenuItem>
                            <MenuItem value="Helvetica">Helvetica</MenuItem>
                            <MenuItem value="Georgia">Georgia</MenuItem>
                            <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                            <MenuItem value="Verdana">Verdana</MenuItem>
                            <MenuItem value="Lucida Sans">Lucida Sans</MenuItem>
                        </TextField>
                    </Grid>
                    {formik.values.headerImage && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                                    Tamaño de imagen de encabezado
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    select
                                    size="small"
                                    label="Tamaño predefinido"
                                    name="headerImageHeight"
                                    value={['400', '560', '700'].includes(formik.values.headerImageHeight) ? formik.values.headerImageHeight : 'custom'}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === 'custom') {
                                            formik.setFieldValue('headerImageHeight', '500');
                                        } else {
                                            formik.setFieldValue('headerImageHeight', value);
                                        }
                                    }}
                                >
                                    <MenuItem value="400">Pequeño (400px)</MenuItem>
                                    <MenuItem value="560">Mediano (560px) - Recomendado</MenuItem>
                                    <MenuItem value="700">Grande (700px)</MenuItem>
                                    <MenuItem value="custom">Personalizado</MenuItem>
                                </TextField>
                            </Grid>
                            {!['400', '560', '700'].includes(formik.values.headerImageHeight) && (
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Altura personalizada (px)"
                                        name="headerImageHeight"
                                        type="number"
                                        placeholder="Ej: 500"
                                        value={formik.values.headerImageHeight}
                                        onChange={formik.handleChange}
                                    />
                                </Grid>
                            )}
                        </>
                    )}
                </Grid>
            </Box>
        </>
    );
};

export default SurveyInformation;
